import _ from "lodash";
import ko from "knockout";
import $ from "jquery";
import moment from "moment";
import "lib/daterangepicker.js";
import datePickerUtil from "bindingHandlers/util/datePickerUtil";

/**
 * The datePicker binding handler is used for functionality that calls for single
 *   date selection
 *
 * The input tag will need a specific class name to differentiate it from other
 *   date range pickers that may be elsewhere on the page. A dropdown containing
 *   the date range picker will be created and appended before the end of the
 *   body. A new dropdown will be created per instance and referenced using the
 *   class of the input tag.
 *
 * # Required
 *   value {Observable} - String, will contain ISO 8601 date
 *
 * # Optional
 *   isTodayMaxDate {Boolean} - default: false; when true, today will be the
 *     maximum date a user can select; prevents any future date from being
 *     selected. Leaving false will allow a user to pick any date in the future.
 *
 *   showMonthYearDropdowns {Boolean} - default: false; when true it enables a
 *     dropdown selection of month and year. Leaving false will disable them.
 *
 *   timePicker {Booelan} - default: false; when true, it enables a
 *     dropdown selection of month and year. Leaving false will disable them.
 *
 *   applyHandler {Object} - default: false; when provided, this adds a custom function
 *     call after the date element is updated via the Apply button. This can
 *     be used to interact with your ViewModel.
 *
 *
 * @example
 *   Minimal example as input:
 *     <input class="classRelatedToParent" data-bind="
 *         datePicker: {  value: startDateValue } "/>
 *
 * @example
 *   Minimal example as div:
 *     <div class="someclassname" data-bind="
 *         datePicker: { value: startDateValue } ">
 *     </div>
 *
 * @example
 *   Full Example with ranges and active month/year dropdowns
 *     <div class="someclassname" data-bind= "
 *         datePicker: {
 *             options: {
 *                 showDropdowns: true,
 *                 isTodayMaxDate: true
 *                 startDate : myStartDate
 *             },
 *             value: dateRangeValue
 *         }
 *     "></div>
 *
 * @example
 *   Full Example with time picker
 *    <div class="someclassname" data-bind="
 *        datePicker: {
 *            options: {
 *                showDropdowns: true,
 *                timePicker: true
 *            },
 *            value: startDateValue
 *        }
 *    "></div>
 *
 * @type {{init: init}}
 */

("use strict");

// Updates the widget UI
const updateUI = function (element, bindingData, updateOptions) {
    const value = ko.utils.domData.get(element, "valueObservable");
    const uiPicker = ko.utils.domData.get(element, "uiPicker");
    const externalValue = value() || bindingData.startDate;
    if (updateOptions) {
        uiPicker.setOptions(
            datePickerUtil.processOptions(bindingData, {
                singleDatePicker: true,
            })
        );
    }
    uiPicker.setStartDate(externalValue);
    uiPicker.setEndDate(externalValue);
};

ko.bindingHandlers.datePicker = {
    init: function (element, valueAccessor) {
        //-------------------------------------------------------------------------
        // Setup
        //-------------------------------------------------------------------------
        const APPLY_EVENT = "apply.daterangepicker";

        // wrap value in an observable if string is provided
        let value = valueAccessor().value;
        if (!ko.isObservable(value)) {
            value = ko.observable(value);
        }
        ko.utils.domData.set(element, "valueObservable", value);

        const bindingData = ko.toJS(valueAccessor());
        ko.utils.domData.set(
            element,
            "currentBindingOptions",
            bindingData.options
        );

        const options = datePickerUtil.processOptions(bindingData, {
            singleDatePicker: true,
        });

        const applyHandler = bindingData.applyHandler
            ? bindingData.applyHandler
            : false;

        const valueUpdateCallback = _.debounce(function (startDate) {
            // Default to start of day only if we're not using a time picker as well
            if (_.isUndefined(options.timePicker) || !options.timePicker) {
                startDate = moment(startDate).startOf("day");
            }

            // Format for value does not output the date intended for display. The date
            //   for display should be derived from the data set via the value format
            const formatted = datePickerUtil.formatForValue(
                startDate,
                options.format
            );

            setValueInternally(formatted);

            // If provided, call our own custom function after the date observable is updated
            //   via the Apply button
            if (applyHandler) {
                applyHandler();
            }
        }, 100);

        const $dateRangePicker = $(element).daterangepicker(options);

        //-------------------------------------------------------------------------
        // External ValueAccessor Behavior (2-way update)
        //   Makes datePicker update ui when valueAccessor is modified externally.
        //--------------------------------------------------------------------------

        // DateRangePicker object from daterangepicker.js lib
        let uiPicker = $(element).data("daterangepicker");
        if ("updateInputText" in bindingData && !bindingData.updateInputText) {
            uiPicker.updateInputText = _.noop;
        }
        ko.utils.domData.set(element, "uiPicker", uiPicker);

        // Flag denoting that the valueAccessor is being modified from within the binding handler
        let internalValueAccessorModificationFlag = false;

        // Subscription to update the ui if the valueAccessor is set to a value (for 2-way binding)
        const externalValueModificationBehavior = value.subscribe(function () {
            if (internalValueAccessorModificationFlag) {
                internalValueAccessorModificationFlag = false;
                return;
            }
            updateUI(element, bindingData, false);
        });

        // Sets valueAccessor internally so you don't need to deal with keeping track of the flag
        function setValueInternally(val) {
            internalValueAccessorModificationFlag = true;
            value(val);
        }
        // END - External ValueAccessor Behavior ------------------------------------

        if (_.isUndefined(bindingData.value)) {
            throw new Error(
                "datePicker binding requires a reference to an observable or an object with a 'value' attribute as its argument."
            );
        }

        // listening to apply event since the default callback is only fired when a change
        //   of date has occurred - making the user have to select a different date - then
        //   the current date - if the user wants the current date.  This is relevant if
        //   the value is not yet set since the internal dateRangePicker moment defaults
        //   to the current date.
        $dateRangePicker.on(APPLY_EVENT, function (event, picker) {
            valueUpdateCallback(picker.startDate);
        });

        datePickerUtil.modifyStyles(element);
        datePickerUtil.handleDisposal(element, function () {
            $dateRangePicker.off(APPLY_EVENT);
            externalValueModificationBehavior.dispose();
        });

        // update UI initially with value stored in valueAccessor
        updateUI(element, bindingData, false);
    },

    update: function (element, valueAccessor) {
        const bindingData = ko.toJS(valueAccessor());
        const currentBindingOptions = ko.utils.domData.get(
            element,
            "currentBindingOptions"
        );
        // update date picker ui if the binding options changed
        if (!_.isEqual(bindingData.options, currentBindingOptions)) {
            ko.utils.domData.set(
                element,
                "currentBindingOptions",
                bindingData.options
            );
            updateUI(element, bindingData, true);
        }
    },
};
